import type { RestTransition } from './restTransition';
import { groupMembersOf } from './supersetNotebook';
import { type DraftEntry, rowIdOf } from './state/loggerDraftTypes';

// #308, the Pin. The athlete pins the exercise on the card, and when the rest
// ends the focus comes back to it instead of going where the timer planned.
// ONE rule, read by the three places that show or apply it (the rest hand-over,
// the dock caption, the roster glyph), so they cannot disagree.
//
// A pin only holds while its row is in the workout and still has a set to
// log: a pinned exercise that is finished, or removed, pins nothing.
// #318: a pin on a superset member pins the whole superset.

export type ActivePin = {
  /** Where a redirected hand-over lands. */
  entry: DraftEntry;
  entryIdx: number;
  /** Set only when the pin covers a superset. */
  groupId?: string;
};

const hasOpenSet = (e: DraftEntry): boolean =>
  e.sets.some((s) => s.completedAt === null);

export function activePin(
  pinnedId: string | null,
  entries: readonly DraftEntry[],
): ActivePin | null {
  if (pinnedId === null) return null;
  const entryIdx = entries.findIndex((e) => rowIdOf(e) === pinnedId);
  const entry = entries[entryIdx];
  if (!entry) return null;
  const groupId = entry.group?.id;
  const members = groupMembersOf(entries, groupId);
  if (!groupId || members.length === 0) {
    return hasOpenSet(entry) ? { entry, entryIdx } : null;
  }
  const target = hasOpenSet(entry)
    ? { entry, entryIdx }
    : members.find((m) => hasOpenSet(m.entry));
  return target ? { entry: target.entry, entryIdx: target.entryIdx, groupId } : null;
}

/** Whether `entry` is under the pin: the pinned row, or any member of the
 *  pinned superset. */
export function pinCovers(pin: ActivePin | null, entry: DraftEntry): boolean {
  if (!pin) return false;
  if (pin.groupId !== undefined) return entry.group?.id === pin.groupId;
  return rowIdOf(entry) === rowIdOf(pin.entry);
}

/** Where the focus goes once the pin is honoured. The same object back when
 *  the pin changes nothing, so a caller can tell "redirected" by identity. */
export function pinnedAdvance(
  transition: RestTransition,
  pinnedId: string | null,
  entries: readonly DraftEntry[],
): RestTransition {
  const pin = activePin(pinnedId, entries);
  if (!pin) return transition;
  if (transition.kind !== 'done') {
    const target = entries.find((e) => rowIdOf(e) === transition.entryId);
    if (target && pinCovers(pin, target)) return transition;
  }
  return {
    kind: 'exercise',
    entryIdx: pin.entryIdx,
    entryId: rowIdOf(pin.entry),
    exerciseId: pin.entry.exerciseId,
    viaGroupMember: false,
    pinned: true,
  };
}

/** The dock caption's transition. A pin that redirects the move names its
 *  target instead of `fallback`. Visibility is NOT decided here: the caller
 *  passes null `running` when LG-70 hides the caption (keyboard, deferral). */
export function pinnedCaption(
  running: RestTransition | null,
  fallback: RestTransition | null,
  pinnedId: string | null,
  entries: readonly DraftEntry[],
): RestTransition | null {
  const pinned = running ? pinnedAdvance(running, pinnedId, entries) : null;
  return pinned && pinned !== running ? pinned : fallback;
}

/** The card's Pin switch for the focused row, or undefined when there is none
 *  to draw: no pin owner (not live logging), or a row with nothing left to
 *  log, where a pin would be dropped at once (usePinnedExercise). On a
 *  superset member it shows and toggles the superset's pin. */
export function focusCardPin(
  pin: { pinnedId: string | null; setPinnedId: (id: string | null) => void } | undefined,
  activeId: string | null,
  entries: readonly DraftEntry[],
): { pinned: boolean; onToggle: () => void } | undefined {
  if (!pin || activeId === null || !activePin(activeId, entries)) return undefined;
  const active = entries.find((e) => rowIdOf(e) === activeId);
  const pinned = active !== undefined && pinCovers(activePin(pin.pinnedId, entries), active);
  return { pinned, onToggle: () => pin.setPinnedId(pinned ? null : activeId) };
}
